import { Injectable } from '@angular/core';
import {
    IFinancialTableData,
    IOptions,
    ISectionData,
    PDFSectionIds,
    RowTypes,
    SectionIds,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumFinancialNumberPipe, DateLocalePipe, NumberUtils } from '@modeso/aumico-lib-core-fe';
import { displayAccountNumber } from '../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassAccountNumber';
import {
    AccountMappingAccountItem,
    AccountType,
    IAccountMappingSubFLI,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAnnualReport,
    IDisplayOptions,
    IFinStatements,
    IPrintOptions,
    ItemType,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { cloneDeep } from 'lodash-es';
import { isFli, isGroup } from '../../../../../../aumico-lib-templates-fe/src/lib';
import { IMappingAndReclassesAccounts } from '../../../interfaces/IMappingAndReclassesAccounts.interface';
import { IPrintPreviewMappingValue } from '../../../interfaces/IPrintPreviewMappingValue.interface';
import { IReferenceValueForRatioCalculation } from '../../../interfaces/IReferenceValueForRatioCalculation.interface';
import { isAccountWithRatio } from '../../../shared/util/accountTypeGuard';
import { localizationKeys } from '../../../utils/localizationKeys';
import { AccountMappingDisplayHelper } from '../helpers/annual-report-account-mapping-group-display-component.helper';
import { PdfTronMapperHelper } from '../helpers/pdf-tron-mapper.helper';
import { GroupingHelper } from '../utils/GroupingHelper';
import { GroupingPathHelper } from '../utils/GroupingPathHelper';
import { MaskingHelper } from '../utils/MaskingHelper';
import { SplitAccountHelper } from '../utils/SplitAccountHelper';
import { PreviousPeriodHelper } from '../helpers/previous-period.helper';

const ONE = 1;

@Injectable()
export class AccountProofMapper {
    constructor(
        private readonly dateLocalPipe: DateLocalePipe,
        private readonly aumFinancialNumberPipe: AumFinancialNumberPipe,
        private readonly accountMappingDisplayHelper: AccountMappingDisplayHelper,
    ) {}

    public mapFinStatementToAccountProof(
        finStatement: IFinStatements,
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        options: IOptions,
        predefinedLabels: Map<string, string>,
        translationLocale: string,
    ): ISectionData {
        const cloned = cloneDeep<IFinStatements>(finStatement);
        // REV-21798: hoist reclassification accounts into `accounts` BEFORE grouping runs.
        // `applyGroupingAndSummarize` only ever walks `accounts`, so a reclass account sitting on
        // `reclassesAccounts` could never be folded into its group — it was appended afterwards by
        // `applyReclasses` and printed OUTSIDE the group. Hoisting also puts it inside its SubFLI's
        // `accounts`, so it prints under that SubFLI. The other two surfaces (the G&M table and the
        // notes pipeline) already hoist at exactly this point; print was the odd one out.
        if (printOptions.displayOptions.applyGroupingMasking && cloned.templateAndAccountsMapping?.template?.group) {
            GroupingHelper.hoistReclassesAccounts(cloned.templateAndAccountsMapping.template.group);
        }
        const groupWithMasking = printOptions.displayOptions.applyGroupingMasking ? GroupingPathHelper.generateAndAddFullSortingState(cloned.templateAndAccountsMapping.template?.group, '') : cloned.templateAndAccountsMapping.template?.group;
        const groupedAccountsMap = annualReport.groupingAndMasking?.groupedAccounts;
        const splitAccountsMap = annualReport.groupingAndMasking?.splitGroupAccounts;
        let groupingMaskingFullyApplied = cloneDeep(groupWithMasking);
        if (groupedAccountsMap && printOptions.displayOptions.applyGroupingMasking) {
            groupingMaskingFullyApplied = GroupingHelper.applyGroupingAndSummarize(
                groupWithMasking,
                groupedAccountsMap,
            );
        }
        if (splitAccountsMap && printOptions.displayOptions.applyGroupingMasking) {
            groupingMaskingFullyApplied = SplitAccountHelper.applySplitAccounts(
                groupingMaskingFullyApplied,
                splitAccountsMap,
            );
            groupingMaskingFullyApplied = GroupingHelper.recalculateGroupSummaries(
                groupingMaskingFullyApplied
            );
        }
        
        // Apply custom sorting from G&M tab
        if (annualReport.groupingAndMasking?.sorting && printOptions.displayOptions.applyGroupingMasking) {
            GroupingHelper.applySortingToAllItems(
                groupingMaskingFullyApplied,
                annualReport.groupingAndMasking.sorting
            );
        }
        
        const accountsMappingTemplateGroups = groupingMaskingFullyApplied.sortedItems;

        if (annualReport.generalInformation == null || printOptions == null) {
            throw new Error('Annual report print options or general information is undefined');
        }
        const noPrevious = PreviousPeriodHelper.hidesPreviousPeriod(
            annualReport.generalInformation,
            printOptions.displayOptions,
        );
        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } =
            PdfTronMapperHelper.getNumberFormatAndDecimalPlaces(
                printOptions.displayOptions,
                printOptions.displayOptions.finStatementHideDecimalPlacesValues,
                printOptions.displayOptions.finStatementHideDecimalPlacesRatios,
                printOptions.displayOptions.finStatementRoundToThousands,
            );

        const finStatementData: IFinancialTableData[] = [];
        const tablesData: IFinancialTableData[][] = [];
        if (accountsMappingTemplateGroups != null) {
            // first we loop on the main groups
            accountsMappingTemplateGroups.forEach((accountsMappingTemplateGroup) => {
                const tableData: IFinancialTableData[] = [];
                const previousDate = noPrevious
                    ? undefined
                    : this.dateLocalPipe.transform(annualReport.generalInformation.balancePreviousYearDate).toString();
                const currentDate = this.dateLocalPipe
                    .transform(annualReport.generalInformation.balanceSheetDate)
                    .toString();

                const tableHeaderFinancialData: IFinancialTableData = {
                    rowType: RowTypes.TABLE_HEAD,
                    t: accountsMappingTemplateGroup.title,
                    pV: noPrevious ? undefined : previousDate,
                    cV: currentDate,
                    extraHeader1: predefinedLabels.get(
                        localizationKeys.aumico_annual_report_account_proof_display_account_number_column_header,
                    ),
                    extraHeader2: predefinedLabels.get(
                        localizationKeys.aumico_annual_report_account_proof_display_account_name_column_header,
                    ),
                    currentDateTitle: predefinedLabels.get(
                        localizationKeys.aumico_annual_report_display_current_date_column_header,
                    ),
                    previousDateTitle: predefinedLabels.get(
                        localizationKeys.aumico_annual_report_display_previous_date_column_header,
                    ),
                };

                const groupMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    accountsMappingTemplateGroup,
                    printOptions.displayOptions.showReClassInSums,
                    printOptions.displayOptions.showAdjustmentInSums,
                );

                const previousValue =
                    groupMappingValues.openingValue != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(groupMappingValues.openingValue, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;
                const currentValue =
                    groupMappingValues.openingValue != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(groupMappingValues.closingValue, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;

                const formattedPreviousValue =
                    previousValue != null
                        ? this.aumFinancialNumberPipe.transform(
                              previousValue,
                              numberFormatValues,
                              printOptions.displayOptions.nonBreakingHyphen,
                          )
                        : null;

                const formattedCurrentValue =
                    currentValue != null
                        ? this.aumFinancialNumberPipe.transform(
                              currentValue,
                              numberFormatValues,
                              printOptions.displayOptions.nonBreakingHyphen,
                          )
                        : null;

                const tableTotalFinancialData: IFinancialTableData = {
                    rowType: RowTypes.TABLE_TOTAL,
                    t: accountsMappingTemplateGroup.totalName,
                    pV: noPrevious ? undefined : formattedPreviousValue,
                    cV: formattedCurrentValue,
                };

                // get the reference for calculating the ratios
                const ratioReferenceOpeningAndClosing = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    accountsMappingTemplateGroup,
                    printOptions.displayOptions.showReClassInSums,
                    printOptions.displayOptions.showAdjustmentInSums,
                );

                const referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation = {
                    openingReferenceValue: ratioReferenceOpeningAndClosing.openingValue,
                    closingReferenceValue: ratioReferenceOpeningAndClosing.closingValue,
                };

                // push the table header
                finStatementData.push(tableHeaderFinancialData);
                tableData.push(tableHeaderFinancialData);
                if (accountsMappingTemplateGroup.sortedItems == null) {
                    throw new Error(`sortedItems array does not exist for group ${accountsMappingTemplateGroup}`);
                }

                const accountsMappingTemplateGroupsData =
                    this.mapAccountsMappingTemplateGroupsToFinancialTableDataAccountProof(
                        accountsMappingTemplateGroup.sortedItems as SortedMappingItem[],
                        annualReport,
                        printOptions,
                        referenceValuesForRatioCalculation,
                        options,
                    );
                finStatementData.push(...accountsMappingTemplateGroupsData);
                tableData.push(...accountsMappingTemplateGroupsData);

                // push the table total at the end to be the last row
                finStatementData.push(tableTotalFinancialData);
                tableData.push(tableTotalFinancialData);
                tablesData.push(tableData);
            });
        }

        const sectionId = PDFSectionIds.APBS;

        return {
            sectionId,
            title:
                (printOptions.reportSectionsTitles != null &&
                printOptions.reportSectionsTitles[SectionIds.APBS] != null &&
                printOptions.reportSectionsTitles[SectionIds.APBS][translationLocale] != null
                    ? printOptions.reportSectionsTitles[SectionIds.APBS][translationLocale]
                    : predefinedLabels.get(
                          localizationKeys.aumico_annual_report_print_preview_account_proof_for_balance,
                      )) +
                this.getThousandsTitleSuffix(annualReport, printOptions, predefinedLabels),
            hasSignature: false,
            data: finStatementData,
            noPrevious,
            showReference: false,
            showRatios: printOptions?.displayOptions.showRatios,
            tables: tablesData,
        };
    }
    public mapIncomeStatementToAccountProof(
        finStatement: IFinStatements,
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        options: IOptions,
        predefinedLabels: Map<string, string>,
        translationLocale: string,
    ): ISectionData {
        const cloned = cloneDeep<IFinStatements>(finStatement);
        // REV-21798: hoist reclassification accounts into `accounts` BEFORE grouping runs.
        // `applyGroupingAndSummarize` only ever walks `accounts`, so a reclass account sitting on
        // `reclassesAccounts` could never be folded into its group — it was appended afterwards by
        // `applyReclasses` and printed OUTSIDE the group. Hoisting also puts it inside its SubFLI's
        // `accounts`, so it prints under that SubFLI. The other two surfaces (the G&M table and the
        // notes pipeline) already hoist at exactly this point; print was the odd one out.
        if (printOptions.displayOptions.applyGroupingMasking && cloned.templateAndAccountsMapping?.template?.group) {
            GroupingHelper.hoistReclassesAccounts(cloned.templateAndAccountsMapping.template.group);
        }
        const groupWithMasking = printOptions.displayOptions.applyGroupingMasking ? GroupingPathHelper.generateAndAddFullSortingState(cloned.templateAndAccountsMapping.template?.group, '') : cloned.templateAndAccountsMapping.template?.group;
        const groupedAccountsMap = annualReport.groupingAndMasking?.groupedAccounts;
        const splitAccountsMap = annualReport.groupingAndMasking?.splitGroupAccounts;
        let groupingMaskingFullyApplied = cloneDeep(groupWithMasking);
        if (groupedAccountsMap && printOptions.displayOptions.applyGroupingMasking) {
            groupingMaskingFullyApplied = GroupingHelper.applyGroupingAndSummarize(
                groupWithMasking,
                groupedAccountsMap,
            );
        }
        if (splitAccountsMap && printOptions.displayOptions.applyGroupingMasking) {
            groupingMaskingFullyApplied = SplitAccountHelper.applySplitAccounts(
                groupingMaskingFullyApplied,
                splitAccountsMap,
            );
            groupingMaskingFullyApplied = GroupingHelper.recalculateGroupSummaries(
                groupingMaskingFullyApplied
            );
        }
        
        // Apply custom sorting from G&M tab
        if (annualReport.groupingAndMasking?.sorting && printOptions.displayOptions.applyGroupingMasking) {
            GroupingHelper.applySortingToAllItems(
                groupingMaskingFullyApplied,
                annualReport.groupingAndMasking.sorting
            );
        }
        
        const accountsMappingTemplateMainGroup = groupingMaskingFullyApplied;
        const accountsMappingTemplateGroups = [groupingMaskingFullyApplied];

        if (annualReport.generalInformation == null) {
            throw new Error("Annual report's general information is null");
        }
        const noPrevious = PreviousPeriodHelper.hidesPreviousPeriod(
            annualReport.generalInformation,
            printOptions.displayOptions,
        );
        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } =
            PdfTronMapperHelper.getNumberFormatAndDecimalPlaces(
                printOptions.displayOptions,
                printOptions.displayOptions.finStatementHideDecimalPlacesValues,
                printOptions.displayOptions.finStatementHideDecimalPlacesRatios,
                printOptions.displayOptions.finStatementRoundToThousands,
            );

        const finStatementData: IFinancialTableData[] = [];

        if (accountsMappingTemplateGroups != null) {
            //First loop through items of the main group (in case of income stateme

            accountsMappingTemplateGroups.forEach((accountsMappingTemplateGroup) => {
                if (annualReport.generalInformation == null || printOptions == null) {
                    throw new Error('Annual report print options or general information is undefined');
                }
                const previousDate = noPrevious
                    ? undefined
                    : this.dateLocalPipe.transform(annualReport.generalInformation.balancePreviousYearDate).toString();
                const currentDate = this.dateLocalPipe
                    .transform(annualReport.generalInformation.balanceSheetDate)
                    .toString();

                const tableHeaderFinancialData: IFinancialTableData = {
                    rowType: RowTypes.TABLE_HEAD,
                    t: accountsMappingTemplateGroup.title,
                    pV: noPrevious ? undefined : previousDate,
                    cV: currentDate,
                };

                const groupMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    accountsMappingTemplateGroup,
                    printOptions.displayOptions.showReClassInSums,
                    printOptions.displayOptions.showAdjustmentInSums,
                );

                const previousValue =
                    groupMappingValues.openingValue != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(groupMappingValues.openingValue, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;
                const currentValue =
                    groupMappingValues.openingValue != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(groupMappingValues.closingValue, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;

                const formattedPreviousValue =
                    previousValue != null
                        ? this.aumFinancialNumberPipe.transform(
                              previousValue,
                              numberFormatValues,
                              printOptions.displayOptions.nonBreakingHyphen,
                          )
                        : null;

                const formattedCurrentValue =
                    currentValue != null
                        ? this.aumFinancialNumberPipe.transform(
                              currentValue,
                              numberFormatValues,
                              printOptions.displayOptions.nonBreakingHyphen,
                          )
                        : null;

                const tableTotalFinancialData: IFinancialTableData = {
                    rowType: RowTypes.TABLE_TOTAL,
                    t: accountsMappingTemplateGroup.totalName,
                    pV: noPrevious ? undefined : formattedPreviousValue,
                    cV: formattedCurrentValue,
                };

                // get the reference for calculating the ratios
                const ratioReferenceOpeningAndClosing = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    accountsMappingTemplateGroup,
                    printOptions.displayOptions.showReClassInSums,
                    printOptions.displayOptions.showAdjustmentInSums,
                );

                const referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation = {
                    openingReferenceValue: ratioReferenceOpeningAndClosing.openingValue,
                    closingReferenceValue: ratioReferenceOpeningAndClosing.closingValue,
                };

                // push the table header
                finStatementData.push(tableHeaderFinancialData);

                if (accountsMappingTemplateGroup.sortedItems == null) {
                    throw new Error(`sortedItems array does not exist for group ${accountsMappingTemplateGroup}`);
                }

                const accountsMappingTemplateGroupsData =
                    this.mapAccountsMappingTemplateGroupsToFinancialTableDataAccountProof(
                        accountsMappingTemplateGroup.sortedItems as SortedMappingItem[],
                        annualReport,
                        printOptions,
                        referenceValuesForRatioCalculation,
                        options,
                    );
                finStatementData.push(...accountsMappingTemplateGroupsData);

                // push the table total at the end to be the last row
                finStatementData.push(tableTotalFinancialData);
            });
        }

        const sectionId = PDFSectionIds.APIS;

        return {
            sectionId,
            title:
                (printOptions.reportSectionsTitles != null &&
                printOptions.reportSectionsTitles[SectionIds.APIS] != null &&
                printOptions.reportSectionsTitles[SectionIds.APIS][translationLocale] != null
                    ? printOptions.reportSectionsTitles[SectionIds.APIS][translationLocale]
                    : predefinedLabels.get(
                          localizationKeys.aumico_annual_report_print_preview_account_proof_for_income,
                      )) +
                this.getThousandsTitleSuffix(annualReport, printOptions, predefinedLabels),
            hasSignature: false,
            data: finStatementData,
            noPrevious,
            showReference: false,
            showRatios: printOptions?.displayOptions.showRatios,
        };
    }

    private mapAccountsMappingTemplateGroupsToFinancialTableDataAccountProof(
        sortedItems: SortedMappingItem[],
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation,
        options: IOptions,
    ): IFinancialTableData[] {
        const financialData: IFinancialTableData[] = [];

        if (printOptions == null || annualReport.generalInformation == null) {
            throw new Error('Print options or general information is null or undefined');
        }
        sortedItems.forEach((sortedItem) => {
            if (printOptions == null || annualReport.generalInformation == null) {
                throw new Error('Print options or general information is null or undefined');
            }

            if (isGroup(sortedItem)) {
                // Only render the group title if the group actually has a (non-zero) account
                // somewhere in its subtree. The account proof always hides zero accounts, so a
                // group with no non-zero accounts renders nothing and must not print a lone title.
                if (sortedItem.title && !sortedItem.isHiddenGroup && this.accountProofGroupHasAccount(sortedItem)) {
                    const groupTitleRow: IFinancialTableData = {
                        rowType: RowTypes.ROW,
                        t: sortedItem.title,
                    };
                    financialData.push(groupTitleRow);
                }

                const groupsFinancialData = this.mapAccountsMappingTemplateGroupsToFinancialTableDataAccountProof(
                    sortedItem.sortedItems as SortedMappingItem[],
                    annualReport,
                    printOptions,
                    referenceValuesForRatioCalculation,
                    options,
                );

                financialData.push(...groupsFinancialData);
            } else if (isFli(sortedItem)) {
                // Items of interest: FLIs
                const itemsFinancialData = this.mapAccountsMappingTemplateGroupItemToFinancialTableDataAccountProof(
                    sortedItem,
                    annualReport,
                    printOptions,
                    referenceValuesForRatioCalculation,
                    options.printClosingValueTotalsInSeparateColumn,
                );
                financialData.push(...itemsFinancialData);
            }
        });
        return financialData;
    }

    /**
     * True if the group has at least one real, non-zero account anywhere in its subtree
     * (recursing nested groups, FLIs and sub-FLIs). Mirrors the account-proof `filterAccounts`
     * rule: an account is "real" when its `type` is not a structural ItemType and it is non-zero.
     * Used so empty groups do not print a lone title in the account proof.
     */
    private accountProofGroupHasAccount(group: IAccountMappingTemplateGroup): boolean {
        const items = group.sortedItems as SortedMappingItem[] | undefined;
        if (items == null) {
            return false;
        }
        return items.some((item) => {
            if (isGroup(item)) {
                return this.accountProofGroupHasAccount(item);
            }
            if (isFli(item)) {
                return (item.accounts ?? []).some((account) => this.accountProofEntryHasAccount(account));
            }
            return false;
        });
    }

    private accountProofEntryHasAccount(entry: IAccountMappingTemplateGroupItemFLI['accounts'][number]): boolean {
        const structuralTypes: string[] = Object.values(ItemType);
        const e = entry as { type?: string; openingBalance?: number; closingBalance?: number };
        const isRealNonZeroAccount =
            !structuralTypes.includes(e.type as string) && !(e.openingBalance === 0 && e.closingBalance === 0);
        if (isRealNonZeroAccount) {
            return true;
        }
        const nested = (entry as IAccountMappingSubFLI).accounts;
        return nested != null && nested.some((n) => this.accountProofEntryHasAccount(n));
    }

    private mapAccountsMappingTemplateGroupItemToFinancialTableDataAccountProof(
        item: IAccountMappingTemplateGroupItemFLI,
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation,
        printClosingValueTotalsInSeparateColumn: boolean,
    ): IFinancialTableData[] {
        const financialData: IFinancialTableData[] = [];

        if (printOptions == null || annualReport.generalInformation == null) {
            throw new Error('Print options or general information is null or undefined');
        }

        const noPrevious = PreviousPeriodHelper.hidesPreviousPeriod(
            annualReport.generalInformation,
            printOptions.displayOptions,
        );

        let showFliTotal = true;
        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } =
            PdfTronMapperHelper.getNumberFormatAndDecimalPlaces(
                printOptions.displayOptions,
                printOptions.displayOptions.finStatementHideDecimalPlacesValues,
                printOptions.displayOptions.finStatementHideDecimalPlacesRatios,
                printOptions.displayOptions.finStatementRoundToThousands,
            );

        if (printOptions == null) {
            throw new Error('Print options is null or undefined');
        }

        //Test if has Accounts or Subflis
        if (item.accounts.length == 0) return [];

        const noAccountTypes: string[] = Object.values(ItemType);
        const filterAccounts = (account) => {
            return (
                !noAccountTypes.includes(account.type) &&
                !(account.openingBalance === 0 && account.closingBalance === 0)
            );
        };
        const filterSubfli = (account) => [ItemType.SUB_FLI, ItemType.OF_THAT_ITEM].includes(account.type);

        // Test if has Accounts mapped in Item
        let hasAccountsInItem = false;
        if (item.accounts.filter(filterAccounts).length > 0) hasAccountsInItem = true;

        // Test if has Subflis mapped in Item
        let hasSubflisInItem = false;
        if (item.accounts.filter(filterSubfli).length > 0) hasSubflisInItem = true;

        // Test if has Accounts mapped in Subflis
        const subFLIAccounts: IAccountMappingSubFLI[] = item.accounts.filter(
            filterSubfli,
        ) as unknown as IAccountMappingSubFLI[];
        let hasAccountsInSubFLIs = false;
        for (let subFLIAccount of subFLIAccounts) {
            if (subFLIAccount.accounts && subFLIAccount.accounts.filter(filterAccounts).length > 0) {
                // No valid accounts in this SUB_FLI account
                hasAccountsInSubFLIs = true;
                break;
            }
        }

        let itemMappingValues: IPrintPreviewMappingValue | undefined;
        itemMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
            item,
            printOptions.displayOptions.showReClassInSums,
            printOptions.displayOptions.showAdjustmentInSums,
        );

        const previousValue =
            itemMappingValues != null && itemMappingValues.openingValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(itemMappingValues.openingValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const currentValue =
            itemMappingValues != null && itemMappingValues.closingValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(itemMappingValues.closingValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedPreviousValue =
            previousValue != null
                ? this.aumFinancialNumberPipe.transform(
                      previousValue,
                      numberFormatValues,
                      printOptions.displayOptions.nonBreakingHyphen,
                  )
                : null;
        const formattedCurrentValue =
            currentValue != null
                ? this.aumFinancialNumberPipe.transform(
                      currentValue,
                      numberFormatValues,
                      printOptions.displayOptions.nonBreakingHyphen,
                  )
                : null;
        const itemRow: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t: item.caption,
            pV: noPrevious || !showFliTotal ? undefined : formattedPreviousValue,
            cV: !showFliTotal ? undefined : formattedCurrentValue,
            cF: printClosingValueTotalsInSeparateColumn ? formattedCurrentValue : undefined,
        };

        const subflis = item.accounts.filter(filterSubfli);

        if (hasAccountsInItem) {
            financialData.push(itemRow);

            //Get all accounts of OF THAT Items
            const siblingAccounts = [];
            subflis.forEach((subfli: IAccountMappingSubFLI) => {
                // REV-21798: include a SubFLI whose only content is a reclassification account —
                // those live on `reclassesAccounts`, never in `accounts`.
                if (subfli.accounts.length > 0 || (subfli.reclassesAccounts?.length ?? 0) > 0) {
                    const ofThatMappingAndReclassesAccounts: IMappingAndReclassesAccounts = {
                        mappedAccounts: subfli.accounts,
                        reclassesAccounts: subfli.reclassesAccounts,
                    };
                    siblingAccounts.push(ofThatMappingAndReclassesAccounts);
                }
            });

            // Initialize arrays to hold the combined accounts and reclasses
            let combinedMappedAccounts = [];
            let combinedReclassesAccounts = [];

            // Add the accounts from the current item
            combinedMappedAccounts = combinedMappedAccounts.concat(item.accounts);
            combinedReclassesAccounts = combinedReclassesAccounts.concat(item.reclassesAccounts);

            // Loop through each siblingAccounts to add their accounts
            siblingAccounts.forEach((account) => {
                combinedMappedAccounts = combinedMappedAccounts.concat(account.mappedAccounts);
                combinedReclassesAccounts = combinedReclassesAccounts.concat(account.reclassesAccounts);
            });

            // Create the final structure with all combined accounts
            const mappingAndReclassesAccounts = {
                mappedAccounts: combinedMappedAccounts,
                reclassesAccounts: combinedReclassesAccounts,
            };
            const accountsFinancialData = this.mapAccountsToFinancialTableDataAccountProof(
                mappingAndReclassesAccounts,
                annualReport,
                printOptions.displayOptions,
                noPrevious,
                referenceValuesForRatioCalculation,
                printClosingValueTotalsInSeparateColumn,
                formattedCurrentValue,
                formattedPreviousValue,
            );
            financialData.push(...accountsFinancialData);
        }

        if (hasSubflisInItem && hasAccountsInSubFLIs) {
            if (!hasAccountsInItem) {
                financialData.push(itemRow);
            }

            subflis.forEach((subfli: IAccountMappingSubFLI) => {
                let itemMappingValues: IPrintPreviewMappingValue | undefined;
                itemMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    subfli,
                    printOptions.displayOptions.showReClassInSums,
                    printOptions.displayOptions.showAdjustmentInSums,
                );

                const previousValue =
                    itemMappingValues != null && itemMappingValues.openingValue != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(itemMappingValues.openingValue, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;
                const currentValue =
                    itemMappingValues != null && itemMappingValues.closingValue != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(itemMappingValues.closingValue, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;

                const formattedPreviousValue =
                    previousValue != null
                        ? this.aumFinancialNumberPipe.transform(
                              previousValue,
                              numberFormatValues,
                              printOptions.displayOptions.nonBreakingHyphen,
                          )
                        : null;
                const formattedCurrentValue =
                    currentValue != null
                        ? this.aumFinancialNumberPipe.transform(
                              currentValue,
                              numberFormatValues,
                              printOptions.displayOptions.nonBreakingHyphen,
                          )
                        : null;

                const subFliRow: IFinancialTableData = {
                    rowType: RowTypes.ROW,
                    t: subfli.caption,
                    pV: noPrevious || !showFliTotal ? undefined : formattedPreviousValue,
                    cV: !showFliTotal ? undefined : formattedCurrentValue,
                };
                // REV-21798: same as above — without the reclassesAccounts check, a SubFLI holding
                // only a reclassification account printed neither its own caption row nor the
                // account.
                if (subfli.accounts.length > 0 || (subfli.reclassesAccounts?.length ?? 0) > 0) {
                    financialData.push(subFliRow);
                    const mappingAndReclassesAccounts: IMappingAndReclassesAccounts = {
                        mappedAccounts: subfli.accounts,
                        reclassesAccounts: subfli.reclassesAccounts,
                    };
                    const accountsFinancialData = this.mapAccountsToFinancialTableDataAccountProof(
                        mappingAndReclassesAccounts,
                        annualReport,
                        printOptions.displayOptions,
                        noPrevious,
                        referenceValuesForRatioCalculation,
                        printClosingValueTotalsInSeparateColumn,
                        formattedCurrentValue,
                        formattedPreviousValue,
                    );
                    financialData.push(...accountsFinancialData);
                }
            });
        }

        return financialData;
    }

    private mapAccountsToFinancialTableDataAccountProof(
        accounts: IMappingAndReclassesAccounts,
        annualReport: IAnnualReport,
        printDisplayOptions: IDisplayOptions,
        noPrevious: boolean,
        referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation,
        printClosingValueTotalsInSeparateColumn: boolean,
        itemFormattedCurrentValue: string,
        itemFormattedPreviousValue: string,
    ): IFinancialTableData[] {
        const accountsFinancialData: IFinancialTableData[] = [];

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } =
            PdfTronMapperHelper.getNumberFormatAndDecimalPlaces(
                printDisplayOptions,
                printDisplayOptions.finStatementHideDecimalPlacesValues,
                printDisplayOptions.finStatementHideDecimalPlacesRatios,
                printDisplayOptions.finStatementRoundToThousands,
            );

        // this ignores the sub flis so we have to handle the sub flis separatly
        const updatedAccounts = this.accountMappingDisplayHelper.applyPrintOptionsOnAccounts(
            printDisplayOptions,
            accounts,
        );

        const accountsPrintPreviewMappingDisplay = this.accountMappingDisplayHelper.calculateAccountsRatios(
            updatedAccounts,
            referenceValuesForRatioCalculation,
        );

        const shouldBeDisplayedInForeColumn = this.shouldBeDisplayedInForeColumn(
            updatedAccounts,
            printClosingValueTotalsInSeparateColumn,
        );

        for (const account of accountsPrintPreviewMappingDisplay) {
            if (isAccountWithRatio(account)) {
                const previousValue =
                    account.calculatedOpeningBalance != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(account.calculatedOpeningBalance, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;
                const currentValue =
                    account.calculatedClosingBalance != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(account.calculatedClosingBalance, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;

                const formattedPreviousValue =
                    previousValue != null
                        ? this.aumFinancialNumberPipe.transform(
                              previousValue,
                              numberFormatValues,
                              printDisplayOptions.nonBreakingHyphen,
                          )
                        : null;
                const formattedCurrentValue =
                    currentValue != null
                        ? this.aumFinancialNumberPipe.transform(
                              currentValue,
                              numberFormatValues,
                              printDisplayOptions.nonBreakingHyphen,
                          )
                        : null;

                const maskedAccountName = MaskingHelper.getAccountDisplayName(
                    account,
                    undefined,
                    annualReport.groupingAndMasking?.renamedAccounts,
                    false,
                    printDisplayOptions.showAccountNumbers,
                    printDisplayOptions.applyGroupingMasking
                );

                // For grouped accounts (summary accounts), hide the account number column
                // because it contains the internal groupId which should not be displayed
                const isGroupedAccount = (account as any).groupId !== undefined;
                
                const accountSubRow: IFinancialTableData = {
                    rowType: RowTypes.SUB_ROW,
                    r:
                        account.type !== AccountType.RECLASSES &&
                        account.type !== AccountType.UNCOVERED_SHORTFALL &&
                        !isGroupedAccount
                            // REV-21798: a system-created reclassification account prints as
                            // `MAN`; its generated number is internal identity only.
                            ? displayAccountNumber(account.accountNumber)
                            : '',
                    t: maskedAccountName,
                    pV: noPrevious ? undefined : formattedPreviousValue,
                    cV: !shouldBeDisplayedInForeColumn ? formattedCurrentValue : undefined,
                    cF: shouldBeDisplayedInForeColumn ? formattedCurrentValue : undefined,
                };

                accountsFinancialData.push(accountSubRow);
            }
        }

        if (shouldBeDisplayedInForeColumn) {
            const extraTotalRow: IFinancialTableData = {
                rowType: RowTypes.SUB_ROW,
                r: undefined,
                t: undefined,
                cV: itemFormattedCurrentValue,
                pV: noPrevious ? undefined : itemFormattedPreviousValue,
            };
            accountsFinancialData.push(extraTotalRow);
        }

        return accountsFinancialData;
    }

    private shouldBeDisplayedInForeColumn(
        accounts: AccountMappingAccountItem[],
        printClosingValueTotalsInSeparateColumn: boolean,
    ): boolean {
        if (!printClosingValueTotalsInSeparateColumn) {
            return false;
        }
        if (accounts.length > ONE) {
            return true;
        }
        return false;
    }

    /**
     * The account proof has no currency in its table head, so "in TCHF" goes into the section
     * title when the financial statements print in thousands (REV-22111); otherwise nothing changes.
     */
    private getThousandsTitleSuffix(
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        predefinedLabels: Map<string, string>,
    ): string {
        if (!printOptions.displayOptions.finStatementRoundToThousands) {
            return '';
        }
        const unit = PdfTronMapperHelper.getCurrencyLabel(annualReport.generalInformation?.currency?.name, true);
        if (unit == null) {
            return '';
        }
        const wordBeforeCurrency = predefinedLabels.get(
            localizationKeys.aumico_reports_annualreport_page_print_account_mapping_label_word_before_currency,
        );
        return ' ' + wordBeforeCurrency + ' ' + unit;
    }
}
